import React from 'react'
import {
    BiCog,
    BiCopy,
    BiRefresh,
    BiInfoCircle,
    BiLinkExternal,
    BiListCheck,
} from 'react-icons/bi'
import { navigate } from 'gatsby'
import { Icon } from '@chakra-ui/icons'
import { MenuGroup } from '@chakra-ui/react'
import { MenuItem as ChakraMenuItem } from '@chakra-ui/react'
import { useClipboard, useToast } from '@chakra-ui/react'
import { Box, Text, Link } from '@chakra-ui/react'

import { useUserContext } from '~config/user-context'
import MenuItem from '~components/shared/menu/menu-item'
import PmsTag from '~components/shared/tags/pms-tag'
import { AdminRoutes, UserRoutes } from '~config/routes'
import {
    ClinicFieldsFragment,
    ClinicGroupFieldsFragment,
} from '~graphql/generated/graphql'
import SharedStack from '~components/shared/wrappers/shared-stack'
import { getSupabaseLink, SupabaseEntities } from '~config/constants'
import { formatClinicName, getAddressStrings } from '~utils/helpers'

interface Props {
    clinic: ClinicFieldsFragment
    setClinic: (e: ClinicFieldsFragment | undefined) => void
    setClinicGroup: (e: ClinicGroupFieldsFragment | undefined) => void
    isClinicAdmin: boolean
    canSwitch?: boolean
}

export default function ClinicOptions({
    clinic,
    setClinic,
    setClinicGroup,
    isClinicAdmin,
    canSwitch = true,
}: Props) {
    const { isAdmin } = useUserContext()

    return (
        <>
            <SharedStack row fit px={4} pt={1} spacing={4} mb={2}>
                <Box>
                    <Text fontSize="sm" fontWeight="semibold">
                        {formatClinicName(clinic.name)}
                    </Text>
                    {getAddressStrings(clinic.address).map(a => (
                        <Text key={a} fontSize="10px" color="gray.500">
                            {a}
                        </Text>
                    ))}
                </Box>
            </SharedStack>
            {(isClinicAdmin || isAdmin) && (
                <MenuItem
                    icon={BiInfoCircle}
                    onClick={() => navigate(UserRoutes.ClinicInfo)}
                >
                    View Info
                </MenuItem>
            )}
            {(canSwitch || isAdmin) && (
                <MenuItem
                    icon={BiRefresh}
                    onClick={() => {
                        setClinic(undefined)
                        setClinicGroup(undefined)
                        navigate(UserRoutes.SelectClinic)
                    }}
                >
                    Switch Clinic
                </MenuItem>
            )}
            {isAdmin && (
                <MenuGroup title="Admin" fontSize="xs">
                    <SharedStack row fit px={4} py={1}>
                        <PmsTag pms={clinic.pms} />
                    </SharedStack>
                    <CopyableId label="Clinic ID" value={clinic.id} />
                    {clinic.pms_db && (
                        <CopyableId
                            label="PMS DB ID"
                            value={clinic.pms_db.id}
                        />
                    )}
                    <Link
                        variant="unstyled"
                        href={getSupabaseLink(
                            SupabaseEntities.Clinic,
                            clinic.id,
                        )}
                        isExternal
                    >
                        <MenuItem icon={BiLinkExternal}>
                            View in Supabase
                        </MenuItem>
                    </Link>
                    <Link
                        variant="unstyled"
                        href={AdminRoutes.OnboardingWizard(clinic.id)}
                    >
                        <MenuItem icon={BiListCheck}>Onboarding</MenuItem>
                    </Link>
                    <Link
                        variant="unstyled"
                        href={AdminRoutes.EditClinic(clinic.id)}
                    >
                        <MenuItem icon={BiCog}>Admin Settings</MenuItem>
                    </Link>
                </MenuGroup>
            )}
        </>
    )
}

interface CopyableIdProps {
    label: string
    value: string
}

function CopyableId({ label, value }: CopyableIdProps) {
    const { onCopy } = useClipboard(value)
    const toast = useToast()

    return (
        <ChakraMenuItem
            px={4}
            bg="transparent"
            closeOnSelect={false}
            _hover={{ bg: 'gray.50' }}
            _active={{ bg: 'gray.100' }}
            icon={<Icon as={BiCopy} fontSize="md" />}
            onClick={() => {
                onCopy()
                toast({
                    title: `Copied ${label}`,
                    status: 'success',
                    duration: 1500,
                    isClosable: true,
                })
            }}
        >
            <SharedStack spacing={0}>
                <Text fontSize="10px" fontWeight="medium" color="gray.500">
                    {label}
                </Text>
                <Text fontSize="xs" fontWeight="medium" fontFamily="mono">
                    {value}
                </Text>
            </SharedStack>
        </ChakraMenuItem>
    )
}
